'use client';

import { Input } from '@app/app/_components/Input/Input';
import { AddressSearchInput } from '@app/app/_components/AddressInput/AddressSearchInput';
import { Heading } from '@app/app/_components/Heading/Heading';
import { useTranslations } from 'next-intl';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseHomeInsuranceResult } from '@app/service/eshop/home/actions/purchaseHomeInsurance';
import {
    PolicyholderFormFields,
    PolicyholderFormHandlers,
} from '@app/app/esales/home/_sections/HomeInsuranceWizardSection/hooks/usePolicyholderFormFields';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { preventPasting } from '@app/lib/event-handlers/preventPasting';
import { preventDropping } from '@app/lib/event-handlers/preventDropping';
import { Tenant } from '@app/infrastructure/tenant/model/Tenant';
import { useIsChromiumBrowser } from '@app/lib/browser/useIsChromiumBrowser';

interface Props {
    formState: FormState<PurchaseHomeInsuranceResult>;
    personalCode: string;
    policyholderFields: PolicyholderFormFields;
    policyholderHandlers: PolicyholderFormHandlers;
    tenant: Tenant;
}

export function PolicyholderInformationStep({
    formState,
    personalCode,
    policyholderFields,
    policyholderHandlers,
    tenant,
}: Props) {
    const t = useTranslations();
    const isChromium = useIsChromiumBrowser();

    return (
        <>
            <div className="flex flex-col gap-4">
                <Heading level="h4">{t('eshop.home.personalInformation')}</Heading>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Input
                        name="policyholderFirstName"
                        label={t('eshop.home.firstNameLabel')}
                        placeholder={t('eshop.home.firstNamePlaceholder')}
                        value={policyholderFields.firstName}
                        onChange={policyholderHandlers.handleFirstNameChange}
                        noNumbers
                        error={formState.violations?.policyholderFirstName}
                    />
                    <Input
                        name="policyholderLastName"
                        label={t('eshop.home.lastNameLabel')}
                        placeholder={t('eshop.home.lastNamePlaceholder')}
                        value={policyholderFields.lastName}
                        onChange={policyholderHandlers.handleLastNameChange}
                        noNumbers
                        error={formState.violations?.policyholderLastName}
                    />
                    <ReadOnlyField label={t('eshop.home.personalCodeLabel')}>{personalCode}</ReadOnlyField>
                </div>
            </div>

            <div className="flex flex-col gap-4">
                <Heading level="h4">{t('eshop.home.contactInformation')}</Heading>
                <AddressSearchInput
                    defaultCountry={tenant.toLowerCase()}
                    violations={formState.violations}
                    defaultValue={policyholderFields.address ?? undefined}
                    onChange={policyholderHandlers.handleAddressChange}
                />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Input
                    name="policyholderPhoneNumber"
                    label={t('eshop.home.phoneNumberLabel')}
                    placeholder={t('eshop.home.phoneNumberPlaceholder')}
                    value={policyholderFields.phoneNumber}
                    onChange={policyholderHandlers.handlePhoneNumberChange}
                    prefix="+370"
                    error={formState.violations?.policyholderPhoneNumber}
                />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Input
                    name="policyholderEmail"
                    label={t('eshop.home.emailLabel')}
                    placeholder={t('eshop.home.emailPlaceholder')}
                    value={policyholderFields.email}
                    onChange={policyholderHandlers.handleEmailChange}
                    error={formState.violations?.policyholderEmail}
                    autoComplete={isChromium ? 'chrome-off' : 'off'}
                />
                <Input
                    name="policyholderEmailConfirmation"
                    label={t('eshop.home.emailConfirmationLabel')}
                    placeholder={t('eshop.home.emailPlaceholder')}
                    value={policyholderFields.emailConfirmation}
                    onChange={policyholderHandlers.handleEmailConfirmationChange}
                    onPaste={preventPasting}
                    onDrop={preventDropping}
                    autoComplete={isChromium ? 'chrome-off' : 'off'}
                    error={formState.violations?.policyholderEmailConfirmation}
                />
            </div>
        </>
    );
}
